import { useQuery } from '@tanstack/react-query';
import { dashboardAPI } from '../services/api';
import { useOrganization } from './useOrganization';
import { useAuth } from '../context/AuthContext';

export function useDashboardMetrics() {
  const { canViewAnalytics } = useOrganization();
  const { user, organizationId } = useAuth();

  // Debug initial state
  console.group('useDashboardMetrics Init');
  console.log('Initial State:', {
    canViewAnalytics,
    userId: user?.id,
    organizationId,
    userPermissions: user?.permissions
  });
  console.groupEnd();

  if (!user || !organizationId) {
    console.warn('useDashboardMetrics: Missing user or organizationId');
    return {
      emailMetrics: null,
      orgStats: null,
      isLoading: false,
      error: new Error('Missing required authentication data'),
      hasOrgData: false,
      hasEmailMetrics: false,
      loadingEmailMetrics: false,
      loadingOrgStats: false
    };
  }

  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['dashboardStats', organizationId],
    queryFn: async () => {
      console.log('Fetching dashboard stats:', { organizationId });
      try {
        // Use the analytics endpoint instead of stats
        const response = await dashboardAPI.getAnalytics(organizationId);
        console.log('Dashboard API Raw Response:', response);
        return response;
      } catch (err) {
        console.error('Dashboard API Error:', err);
        throw err;
      }
    },
    enabled: Boolean(organizationId),
    select: (responseData) => {
        // responseData now contains organizationStats and userStats.
        const emailMetrics = {
          totalEmails: responseData.userStats.totalEmails,
          verifiedEmails: responseData.userStats.verifiedEmails,
          dailyEmails: responseData.userStats.dailyEmails,
          verificationRate: responseData.userStats.successRate,
          timelineData: responseData.userStats.timelineData
        };
      
        const orgStats = {
          activeUsers: responseData.organizationStats.activeUsers,
          totalEmails: responseData.organizationStats.totalEmails,
          verifiedEmails: responseData.organizationStats.verifiedEmails,
          verificationRate: responseData.organizationStats.verificationRate
        };
      
        console.log('Processed Metrics:', { orgStats, emailMetrics });
        return { orgStats, emailMetrics };
      }
      
  });

  const result = {
    emailMetrics: data?.emailMetrics,
    orgStats: data?.orgStats,
    isLoading,
    error,
    hasOrgData: canViewAnalytics && Boolean(data?.orgStats),
    hasEmailMetrics: Boolean(data?.emailMetrics),
    loadingEmailMetrics: isLoading,
    loadingOrgStats: canViewAnalytics ? isLoading : false
  };

  // Debug final state
  console.group('useDashboardMetrics Result');
  console.log('Final State:', result);
  console.groupEnd();

  return result;
}